import { isNil, uniqBy } from 'lodash';
import {
  IDictionaryBasicEventFieldsFragment,
  IEventConditionFieldsFragment,
  IVirtualEventFieldsFragment,
} from '../../../apollo/graphqlSchema';
import { IAnalyticsEventEventProperties } from './types';
import { getCreator } from '../user/utils';
import { ElementOf, UnreachableCaseError } from 'ts-essentials';
import { assertCondition } from '../../../types';
import { getFieldNamesFromFilter } from '../filter/utils';
import {
  getFieldName,
  uniqueAnalyticsLoggedFieldFunction,
} from '../field/utils';
import { MaybeNil } from '../../../interfaces';
import { IAnalyticsLoggedEvent, IAnalyticsLoggedField } from '../../types';

export const hasEventFilter = (
  eventConditions: Pick<IEventConditionFieldsFragment, 'filter'>[],
) => {
  return eventConditions.some(
    (ec) => !isNil(ec.filter) && ec.filter.conditions.length > 0,
  );
};

const getEventNamesFromVirtualEvent = (event: IVirtualEventFieldsFragment) =>
  event.eventsConditions.flatMap((ec) =>
    ec.events.flatMap((e) => {
      // can't reuse getEventNamesFromEventCondition because eventConditions.events on Virtual Event does not
      // pull VirtualEvent fields (and it doesn't have to - virtual events can't use other virtual events)
      assertCondition(e.__typename !== 'VirtualEvent');
      return getEventNames(e);
    }),
  );

const getEventNames = (
  event: ElementOf<IEventConditionFieldsFragment['events']>,
): IAnalyticsLoggedEvent[] => {
  switch (event.__typename) {
    case 'VirtualEvent':
      return getEventNamesFromVirtualEvent(event);
    case 'BasicEventValue':
      return [
        {
          key: event.value.key,
          name: event.value.name,
        },
      ];
    case 'AnyEvent':
    case 'AnyTableEvent':
      return [
        {
          key: event.name,
          name: event.name,
        },
      ];
    default:
      throw new UnreachableCaseError(event);
  }
};

const getFieldNamesFromFilterAndField = (
  ec: Pick<IEventConditionFieldsFragment, 'filter' | 'field'>,
) => {
  const fields: IAnalyticsLoggedField[] = [];
  if (!isNil(ec.filter)) {
    fields.push(...getFieldNamesFromFilter(ec.filter));
  }
  if (!isNil(ec.field)) {
    fields.push(getFieldName(ec.field));
  }
  return fields;
};

const getFieldNamesFormVirtualEvent = (event: IVirtualEventFieldsFragment) => {
  return event.eventsConditions.flatMap(getFieldNamesFromFilterAndField);
};

const getFieldNamesFromEvent = (
  event: ElementOf<IEventConditionFieldsFragment['events']>,
) => {
  switch (event.__typename) {
    case 'VirtualEvent':
      return getFieldNamesFormVirtualEvent(event);
    case 'BasicEventValue':
    case 'AnyEvent':
    case 'AnyTableEvent':
      return [];
    default:
      throw new UnreachableCaseError(event);
  }
};

export const getEventNamesFromEventCondition = (
  ec: MaybeNil<IEventConditionFieldsFragment>,
) => (!isNil(ec) ? ec.events.flatMap(getEventNames) : []);

export const getFieldNamesFromEventCondition = (
  ec: MaybeNil<IEventConditionFieldsFragment>,
) => {
  if (isNil(ec)) {
    return [];
  }
  const fields = getFieldNamesFromFilterAndField(ec);
  fields.push(...ec.events.flatMap(getFieldNamesFromEvent));
  return fields;
};

export const getEventNamesFromEventConditionCollection = (
  ec: IEventConditionFieldsFragment[],
) => ec.flatMap(getEventNamesFromEventCondition);

export const getFieldNamesFromEventConditionCollection = (
  ec: IEventConditionFieldsFragment[],
) => ec.flatMap(getFieldNamesFromEventCondition);

export const uniqueAnalyticsLoggedEventFunction = ({
  key,
}: IAnalyticsLoggedEvent) => key;

const getUniqFieldNamesFromEvent = (event: IVirtualEventFieldsFragment) =>
  uniqBy(getFieldNamesFromEvent(event), uniqueAnalyticsLoggedFieldFunction);

const getUniqEventNamesFromEvent = (event: IVirtualEventFieldsFragment) =>
  uniqBy(
    getEventNamesFromVirtualEvent(event),
    uniqueAnalyticsLoggedEventFunction,
  );

const getVirtualEventFields = (
  virtualEvent: IVirtualEventFieldsFragment,
): IAnalyticsEventEventProperties => {
  return {
    object_type: 'virtual event',
    object_owner: getCreator(virtualEvent),
    has_event_filter: hasEventFilter(virtualEvent.eventsConditions),
    schema_name: virtualEvent.schema.name,
    object_name: virtualEvent.name,
    object_id: virtualEvent.id,
    fields: getUniqFieldNamesFromEvent(virtualEvent),
    events: getUniqEventNamesFromEvent(virtualEvent),
  };
};

const getBasicEventEventFields = (
  basicEvent: IDictionaryBasicEventFieldsFragment,
): IAnalyticsEventEventProperties => {
  return {
    object_type: 'basic event',
    object_owner: undefined,
    has_event_filter: undefined,
    schema_name: undefined,
    object_name: basicEvent.name,
    object_id: undefined,
    events: undefined,
    fields: undefined,
  };
};

export const getEventEventProperties = (
  event: IVirtualEventFieldsFragment | IDictionaryBasicEventFieldsFragment,
): IAnalyticsEventEventProperties => {
  if (event.__typename === 'VirtualEvent') {
    return getVirtualEventFields(event);
  } else if (event.__typename === 'DictionaryBasicEvent') {
    return getBasicEventEventFields(event);
  } else {
    throw new UnreachableCaseError(event);
  }
};
